import { nextTick, ref } from "vue";
import { useTimeoutFn } from "@vueuse/core";
import { firstDetail } from "#ce/utils/events";

/**
 * Shared activator-driven combobox state for the text-field-activated
 * `<sp-quantum-select>` pickers (`<sp-omnisearch>`, the provider facet). The
 * activator field's text drives a debounced `searchQuery` (the dropdown filter)
 * while `inputText` reflects what the user typed immediately. Components layer
 * their own side effects on after calling the `apply*` actions.
 *
 * @param {object} [options]
 * @param {number} [options.debounceMs=150] Delay before typed text lands in `searchQuery`.
 * @param {boolean} [options.closeWhenEmpty=false] Close the dropdown when the field is
 *   emptied (typing or `setText("")`); by default an emptied field leaves `open` untouched.
 * @returns {{
 *   inputText: import('vue').Ref<string>,
 *   searchQuery: import('vue').Ref<string>,
 *   open: import('vue').Ref<boolean>,
 *   applyInput: (event: CustomEvent) => string,
 *   applyClear: () => void,
 *   applyOpen: (event: CustomEvent) => boolean,
 *   applyActivatorKeydown: (event: KeyboardEvent, focusList?: (location: 'first' | 'last') => void) => void,
 *   flushSearch: () => void,
 *   setText: (text: string | null | undefined) => void,
 * }}
 */
export function useQuantumSearchField({ debounceMs = 150, closeWhenEmpty = false } = {}) {
  const inputText = ref("");
  const searchQuery = ref("");
  const open = ref(false);

  // Setting `searchQuery` drives the dropdown's re-render; debounce it so fast
  // typing stays smooth (the field shows the typed text natively, so this never
  // lags). A self-managed timer (over useDebounceFn) gives us an explicit `stop`
  // to cancel a pending update when the field is cleared — VueUse 13's
  // useDebounceFn return value is not cancellable.
  let pendingValue = "";
  const { start: startApply, stop: stopApply } = useTimeoutFn(
    () => {
      searchQuery.value = pendingValue;
    },
    debounceMs,
    { immediate: false },
  );

  function applySearch(value) {
    pendingValue = value;
    stopApply();
    startApply();
  }

  /**
   * Handle the activator field's `input` event: hold the typed text, open/close
   * the dropdown, and start the debounced `searchQuery` update.
   *
   * The sp text controls emit `input` for user edits only (programmatic writes
   * mirror silently), so every event applies. A `v-model` on the activator writes
   * `inputText` inside the same event; the write here repeats that value.
   *
   * @param {CustomEvent} event The `input` event from the activator sp-text-field
   *   (the typed text rides in `detail[0]`).
   * @returns {string} The typed text (possibly `""`), for callers layering their
   *   own typing side effects.
   */
  function applyInput(event) {
    const text = firstDetail(event) ?? "";
    inputText.value = text;
    // Typing always opens the dropdown. An emptied field dismisses it when
    // `closeWhenEmpty` is set (the provider-name typeahead); otherwise
    // (omnisearch) the open state is left untouched, so deleting back to empty
    // keeps the idle items showing without re-opening a closed menu. The clear
    // button fires a trailing empty `input` that must not re-open a stale menu.
    if (text.trim()) {
      open.value = true;
    } else if (closeWhenEmpty) {
      open.value = false;
    }
    applySearch(text);
    return text;
  }

  /**
   * Reset the field to its empty state: blank `inputText` and `searchQuery`, and
   * cancel any pending debounced search so a keystroke fired moments before the
   * clear can't land afterwards and repopulate `searchQuery` with stale text.
   * Callers chain their own clear side effects directly after this call.
   *
   * @returns {void}
   */
  function applyClear() {
    stopApply();
    inputText.value = "";
    searchQuery.value = "";
  }

  /**
   * Sync `open` with the dropdown's `update:open` event (the picker closes itself
   * on outside click / Escape / selection).
   *
   * @param {CustomEvent} event The `update:open` event (the open flag rides in `detail[0]`).
   * @returns {boolean} The new open state, so callers can branch on a just-closed menu.
   */
  function applyOpen(event) {
    const isOpen = Boolean(firstDetail(event));
    open.value = isOpen;
    return isOpen;
  }

  /**
   * WAI-ARIA combobox open-on-arrow for a closed, focused activator: ArrowDown /
   * ArrowUp open the list and move roving focus to the first / last option via
   * `focusList`; Alt+ArrowDown opens without moving focus; Alt+ArrowUp is a no-op.
   * While the dropdown is already open the sp-list owns the arrow keys, so this
   * does nothing and lets the event bubble.
   *
   * @param {KeyboardEvent} event The `keydown` event from the activator field.
   * @param {(location: 'first' | 'last') => void} [focusList] Moves the picker's
   *   roving focus to the given end of the option list; called one tick after
   *   opening so the list has mounted. Omit when the caller has no list to focus.
   * @returns {void}
   */
  function applyActivatorKeydown(event, focusList) {
    if (open.value || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) {
      return;
    }

    // Per APG, only Alt+ArrowDown opens a closed combobox; Alt+ArrowUp does nothing.
    if (event.altKey && event.key === "ArrowUp") {
      return;
    }

    // preventDefault stops the page scrolling; stopPropagation keeps the closed-state
    // key off the window listener that any other open sp-list navigates from.
    event.preventDefault();
    event.stopPropagation();
    open.value = true;

    if (event.altKey) {
      return;
    }

    const location = event.key === "ArrowDown" ? "first" : "last";
    nextTick(() => focusList?.(location));
  }

  /**
   * Land the typed text in `searchQuery` immediately, cancelling the pending
   * debounce. For actions that must read search-derived state for the text as
   * typed right now — e.g. Enter committing the dropdown's first result — so
   * the action can never act on a query the debounce hasn't caught up with.
   *
   * @returns {void}
   */
  function flushSearch() {
    stopApply();
    searchQuery.value = inputText.value;
  }

  /**
   * Set the field text without re-triggering a search (e.g. after a selection).
   * Cancels any pending debounced update so a keystroke fired just before the
   * selection can't land afterwards and overwrite `searchQuery` with stale text.
   *
   * @param {string | null | undefined} text The committed text; `null`/`undefined`
   *   are treated as `""`.
   * @returns {void}
   */
  function setText(text) {
    stopApply();
    inputText.value = text ?? "";
    // A programmatic empty is a clear (e.g. the provider panel's clear-all while
    // its suggestion list is open): reset the dropdown filter and, for
    // closeWhenEmpty consumers, close the list — the state work typing the
    // field empty does.
    if (!inputText.value) {
      searchQuery.value = "";
      if (closeWhenEmpty) {
        open.value = false;
      }
    }
  }

  return {
    inputText,
    searchQuery,
    open,
    applyInput,
    applyClear,
    applyOpen,
    applyActivatorKeydown,
    flushSearch,
    setText,
  };
}
